import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import { Toaster } from '@/components/ui/toaster';
import MainLayout from '@/components/Layout/MainLayout';
import ScrollToTop from '@/components/ScrollToTop';
import EmailVerificationModal from '@/components/EmailVerificationModal';
import VerifyEmailPage from '@/components/VerifyEmailPage';
import LandingNew from '@/pages/LandingNew';
import Generate from '@/pages/Generate';
import Profile from '@/pages/Profile';
import Admin from '@/pages/Admin';
import Pricing from '@/pages/Pricing';
import Blog from '@/pages/Blog';
import BlogPost from '@/pages/BlogPost';
import Guides from '@/pages/Guides';
import GuidePage from '@/pages/GuidePage';
import PrivacyPolicy from '@/pages/PrivacyPolicy';
import TermsOfUse from '@/pages/TermsOfUse';
import License from '@/pages/License';
import CookiePolicy from '@/pages/CookiePolicy';
import Features from '@/pages/Features';
import UseCases from '@/pages/UseCases';
import ApiDocs from '@/pages/ApiDocs';
import Support from '@/pages/Support';
import UnsubscribePage from '@/pages/UnsubscribePage';
import '@/App.css';

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { currentUser, userProfile, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600"></div>
      </div>
    );
  }

  if (!currentUser) {
    return <Navigate to="/" replace />;
  }

  if (adminOnly && userProfile?.role !== 'admin') {
    return <Navigate to="/" replace />;
  }

  return children;
};

function AppRoutes() {
  const { currentUser } = useAuth();
  const [showEmailVerificationModal, setShowEmailVerificationModal] = useState(false);
  
  // Listen for signup event to show modal
  useEffect(() => {
    const handleSignupSuccess = () => {
      if (currentUser && !currentUser.emailVerified) {
        setShowEmailVerificationModal(true);
      }
    };
    
    window.addEventListener('signupSuccess', handleSignupSuccess);
    return () => window.removeEventListener('signupSuccess', handleSignupSuccess);
  }, [currentUser]);
  
  return (
    <>
      <EmailVerificationModal
        isOpen={showEmailVerificationModal}
        onClose={() => setShowEmailVerificationModal(false)}
        userEmail={currentUser?.email || ''}
      />
      
      <Routes>
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/" element={<MainLayout />}>
          <Route index element={<LandingNew />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/guides" element={<Guides />} />
          <Route path="/guides/:slug" element={<GuidePage />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfUse />} />
          <Route path="/license" element={<License />} />
          <Route path="/cookie-policy" element={<CookiePolicy />} />
          <Route path="/features" element={<Features />} />
          <Route path="/use-cases" element={<UseCases />} />
          <Route path="/api-docs" element={<ApiDocs />} />
          <Route path="/support" element={<Support />} />
          <Route path="/unsubscribe" element={<UnsubscribePage />} />
          <Route path="/generate" element={<Generate />} />
          <Route path="/projects" element={<Navigate to="/generate" replace />} />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute adminOnly>
                <Admin />
              </ProtectedRoute>
            }
          />
        </Route>
      </Routes>
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ScrollToTop />
        <AppRoutes />
        <Toaster />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;